import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  VirtualViewportEntry,
  VirtualViewportItemContext,
  VirtualViewportTrackBy,
  VirtualViewportVisibleRange,
} from '../virtualization.types';

@Component({
  selector: 'app-virtual-list-viewport',
  templateUrl: './virtual-list-viewport.component.html',
  styleUrls: ['./virtual-list-viewport.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualListViewportComponent<T> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: T[] = [];
  @Input() trackBy: VirtualViewportTrackBy<T> = (index: number) => index;
  @Input() appendDisabled = false;
  @Input() appendTriggerMarginPx = 1200;
  @Input() estimatedItemHeight = 520;
  @Input() minRenderCount = 6;
  @Input() itemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;

  @Output() appendRequested = new EventEmitter<void>();
  @Output() visibleRangeChange = new EventEmitter<VirtualViewportVisibleRange>();

  @ContentChild(TemplateRef) projectedItemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;
  @ViewChild('viewportRoot', { static: true }) viewportRoot!: ElementRef<HTMLElement>;
  @ViewChild('appendSentinel', { static: true }) appendSentinel!: ElementRef<HTMLElement>;
  @ViewChildren('renderedItem', { read: ElementRef }) renderedItems?: QueryList<ElementRef<HTMLElement>>;

  public visibleItems: VirtualViewportEntry<T>[] = [];
  public topSpacerHeight = 0;
  public bottomSpacerHeight = 0;

  private readonly isBrowser: boolean;
  private readonly itemHeights = new Map<string, number>();
  private readonly observedItems = new Map<string, HTMLElement>();
  private resizeObserver: ResizeObserver | null = null;
  private appendObserver: IntersectionObserver | null = null;
  private viewportUpdateScheduled = false;
  private offsets: number[] = [0];
  private averageItemHeight = 520;
  private lastRange: VirtualViewportVisibleRange | null = null;
  private lastAppendTriggerLength = -1;
  private lastAppendDisabled = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get resolvedTemplate(): TemplateRef<VirtualViewportItemContext<T>> | null {
    return this.itemTemplate || this.projectedItemTemplate || null;
  }

  ngAfterViewInit(): void {
    this.renderedItems?.changes.subscribe(() => this.syncItemObservers());
    this.scheduleViewportUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appendDisabled'] && this.lastAppendDisabled && !this.appendDisabled) {
      this.lastAppendTriggerLength = -1;
    }
    this.lastAppendDisabled = this.appendDisabled;

    if (changes['items'] || changes['estimatedItemHeight']) {
      this.averageItemHeight = Math.max(1, Math.round(this.estimatedItemHeight || 520));
      this.rebuildOffsets();
      this.scheduleViewportUpdate();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.appendObserver?.disconnect();
    this.observedItems.clear();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scheduleViewportUpdate();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleViewportUpdate();
  }

  trackVisibleItem(_: number, entry: VirtualViewportEntry<T>): string {
    return entry.key;
  }

  private scheduleViewportUpdate(): void {
    if (this.viewportUpdateScheduled) {
      return;
    }

    this.viewportUpdateScheduled = true;
    const schedule = this.isBrowser && typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 0);

    schedule(() => {
      this.viewportUpdateScheduled = false;
      this.updateViewport();
      this.syncItemObservers();
      this.syncAppendObserver();
      this.maybeRequestAppendFromProximity();
      this.cdr.markForCheck();
    });
  }

  private updateViewport(): void {
    if (!this.items?.length) {
      this.visibleItems = [];
      this.topSpacerHeight = 0;
      this.bottomSpacerHeight = 0;
      this.emitVisibleRange(0, 0);
      return;
    }

    this.rebuildOffsets();

    if (!this.isBrowser) {
      this.applySlice(0, this.items.length);
      return;
    }

    const hostTop = this.getHostTop();
    const viewportTop = Math.max(0, this.getScrollTop() - hostTop);
    const viewportBottom = viewportTop + this.getViewportHeight();
    const overscan = Math.max(1200, this.getViewportHeight() * 2);
    const startIndex = this.findStartIndex(Math.max(0, viewportTop - overscan));
    let endIndex = this.findEndIndex(viewportBottom + overscan);

    if ((endIndex - startIndex) < this.minRenderCount) {
      endIndex = Math.min(this.items.length, startIndex + this.minRenderCount);
    }

    this.applySlice(startIndex, endIndex);
  }

  private applySlice(startIndex: number, endIndex: number): void {
    const safeStart = Math.max(0, Math.min(startIndex, this.items.length));
    const safeEnd = Math.max(safeStart, Math.min(endIndex, this.items.length));
    const totalHeight = this.offsets[this.items.length] || 0;

    this.visibleItems = this.items.slice(safeStart, safeEnd).map((item, offset) => ({
      item,
      index: safeStart + offset,
      key: this.resolveKey(item, safeStart + offset),
    }));
    this.topSpacerHeight = this.offsets[safeStart] || 0;
    this.bottomSpacerHeight = Math.max(0, totalHeight - (this.offsets[safeEnd] || 0));
    this.emitVisibleRange(safeStart, safeEnd);
  }

  private emitVisibleRange(start: number, end: number): void {
    if (this.lastRange?.start === start && this.lastRange?.end === end) {
      return;
    }

    this.lastRange = { start, end };
    this.visibleRangeChange.emit(this.lastRange);
  }

  private rebuildOffsets(): void {
    const nextOffsets = new Array(this.items.length + 1);
    nextOffsets[0] = 0;

    for (let index = 0; index < this.items.length; index++) {
      nextOffsets[index + 1] = nextOffsets[index] + this.resolveHeightForIndex(index);
    }

    this.offsets = nextOffsets;
  }

  private resolveHeightForIndex(index: number): number {
    return this.itemHeights.get(this.resolveKey(this.items[index], index)) || this.averageItemHeight;
  }

  private findStartIndex(offset: number): number {
    let low = 0;
    let high = this.items.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.offsets[mid + 1] || 0) <= offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }

  private findEndIndex(offset: number): number {
    let low = 0;
    let high = this.items.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.offsets[mid] || 0) < offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return Math.max(1, Math.min(this.items.length, low));
  }

  private syncItemObservers(): void {
    if (!this.isBrowser || typeof ResizeObserver === 'undefined') {
      return;
    }

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        let changed = false;

        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          const key = target.dataset['virtualKey'];
          if (!key) {
            return;
          }

          changed = this.captureItemHeight(key, entry.contentRect.height || target.getBoundingClientRect().height) || changed;
        });

        if (!changed) {
          return;
        }

        this.averageItemHeight = this.computeAverageHeight();
        this.rebuildOffsets();
        this.scheduleViewportUpdate();
      });
    }

    const nextObserved = new Map<string, HTMLElement>();
    (this.renderedItems?.toArray() || []).forEach((ref) => {
      const element = ref.nativeElement;
      const key = element.dataset['virtualKey'];
      if (!key) {
        return;
      }

      nextObserved.set(key, element);
      if (this.observedItems.get(key) !== element) {
        this.resizeObserver?.observe(element);
      }
      this.captureItemHeight(key, element.getBoundingClientRect().height);
    });

    this.observedItems.forEach((element, key) => {
      if (nextObserved.get(key) === element) {
        return;
      }
      this.resizeObserver?.unobserve(element);
    });

    this.observedItems.clear();
    nextObserved.forEach((element, key) => this.observedItems.set(key, element));
    this.averageItemHeight = this.computeAverageHeight();
  }

  private captureItemHeight(key: string, nextHeight: number): boolean {
    const normalizedHeight = Math.max(1, Math.ceil(nextHeight));
    const previousHeight = this.itemHeights.get(key) || 0;

    if (Math.abs(previousHeight - normalizedHeight) < 2) {
      return false;
    }

    this.itemHeights.set(key, normalizedHeight);
    return true;
  }

  private computeAverageHeight(): number {
    const measuredHeights = Array.from(this.itemHeights.values()).filter((height) => height > 0);
    if (!measuredHeights.length) {
      return Math.max(240, Math.round(this.estimatedItemHeight || 520));
    }

    const total = measuredHeights.reduce((sum, height) => sum + height, 0);
    return Math.max(240, Math.round(total / measuredHeights.length));
  }

  private syncAppendObserver(): void {
    if (!this.isBrowser || typeof IntersectionObserver === 'undefined') {
      return;
    }

    if (!this.appendObserver) {
      this.appendObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        this.maybeRequestAppendFromProximity();
      }, {
        root: null,
        rootMargin: `${this.appendTriggerMarginPx}px 0px ${this.appendTriggerMarginPx}px 0px`,
        threshold: 0,
      });
    }

    this.appendObserver.observe(this.appendSentinel.nativeElement);
  }

  private maybeRequestAppendFromProximity(): void {
    if (!this.isBrowser || this.appendDisabled || !this.items.length) {
      return;
    }

    const rect = this.appendSentinel.nativeElement.getBoundingClientRect();
    const viewportHeight = this.getViewportHeight();
    const withinAppendZone = rect.top <= viewportHeight + this.appendTriggerMarginPx
      && rect.bottom >= -this.appendTriggerMarginPx;

    if (!withinAppendZone) {
      return;
    }

    if (this.lastAppendTriggerLength === this.items.length) {
      return;
    }

    this.lastAppendTriggerLength = this.items.length;
    this.appendRequested.emit();
  }

  private resolveKey(item: T, index: number): string {
    const key = this.trackBy(index, item);
    if (typeof key === 'string' || typeof key === 'number') {
      return `${key}`;
    }

    return `index:${index}`;
  }

  private getHostTop(): number {
    const rect = this.viewportRoot.nativeElement.getBoundingClientRect();
    return rect.top + this.getScrollTop();
  }

  private getViewportHeight(): number {
    return this.isBrowser
      ? window.innerHeight || this.document.documentElement?.clientHeight || 0
      : 0;
  }

  private getScrollTop(): number {
    return this.isBrowser
      ? window.scrollY || window.pageYOffset || this.document.documentElement?.scrollTop || 0
      : 0;
  }
}
